import { Wall, Room, Point, FloorPlanElement } from '../types';
import { calculateRooms, RoomCalculationContext } from './floorPlanUtils';
import { PIXELS_PER_METER } from '../constants/floorPlanConstants';

// TEMPORARY PERFORMANCE DEBUG - Disable expensive operations
const DISABLE_CALCULATIONS_FOR_DEBUG = false; // RE-ENABLED: Test massive optimization

// 🚀 PERFORMANCE: Enhanced performance monitoring for room calculations
class RoomCalculationPerformanceMonitor {
  private static metrics = {
    totalCalls: 0,
    totalTime: 0,
    averageTime: 0,
    slowCalls: 0,
    skippedCalls: 0,
    wallCountHistogram: new Map<number, number>(),
    lastSlowCallTime: 0,
  };

  static startTiming(): number {
    return performance.now();
  }

  static endTiming(startTime: number, wallCount: number, wasSkipped: boolean = false): void {
    const duration = performance.now() - startTime;
    
    this.metrics.totalCalls++;
    this.metrics.totalTime += duration;
    this.metrics.averageTime = this.metrics.totalTime / this.metrics.totalCalls;
    
    if (wasSkipped) {
      this.metrics.skippedCalls++;
    }
    
    if (duration > 100) { // Slow call threshold
      this.metrics.slowCalls++;
      this.metrics.lastSlowCallTime = duration;
      
      if (duration > 500) {
        console.warn(`🐌 SLOW ROOM CALCULATION: ${duration.toFixed(1)}ms for ${wallCount} walls`);
      }
    }
    
    // Track wall count distribution
    const bucket = Math.floor(wallCount / 10) * 10;
    this.metrics.wallCountHistogram.set(bucket, (this.metrics.wallCountHistogram.get(bucket) || 0) + 1);
    
    // Log performance summary periodically
    if (this.metrics.totalCalls % 20 === 0) {
      this.logPerformanceSummary();
    }
  }

  static logPerformanceSummary(): void {
    const { totalCalls, averageTime, slowCalls, skippedCalls } = this.metrics;
    console.log(`📊 Room Calc Performance: ${totalCalls} calls, ${averageTime.toFixed(1)}ms avg, ${slowCalls} slow, ${skippedCalls} skipped`);
  }

  static getMetrics() {
    return { ...this.metrics };
  }

  static shouldSkipComplexCalculation(wallCount: number): boolean {
    // Adaptive threshold based on recent performance
    const baseThreshold = 50;
    const performanceMultiplier = this.metrics.averageTime > 200 ? 0.7 : 1.0; // Lower threshold if we're running slow
    const adaptiveThreshold = Math.floor(baseThreshold * performanceMultiplier);
    
    return wallCount > adaptiveThreshold;
  }
}

// Advanced cache with size management and LRU eviction
class AdvancedCalculationCache {
  private cache = new Map<string, any>();
  private accessTimes = new Map<string, number>();
  private readonly maxSize: number;

  constructor(maxSize: number = 50) {
    this.maxSize = maxSize;
  }

  get(key: string): any {
    if (this.cache.has(key)) {
      this.accessTimes.set(key, Date.now());
      return this.cache.get(key);
    }
    return null;
  }

  set(key: string, value: any): void {
    if (this.cache.size >= this.maxSize) {
      this.evictLRU();
    }
    this.cache.set(key, value);
    this.accessTimes.set(key, Date.now());
  }

  private evictLRU(): void {
    let oldestKey = '';
    let oldestTime = Infinity;
    
    for (const [key, time] of this.accessTimes) {
      if (time < oldestTime) {
        oldestTime = time;
        oldestKey = key;
      }
    }
    
    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.accessTimes.delete(oldestKey);
    }
  }

  clear(): void {
    this.cache.clear();
    this.accessTimes.clear();
  }

  // 🚀 PERFORMANCE: Add cache statistics
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      hitRate: this.cache.size > 0 ? (this.cache.size / (this.cache.size + 1)) : 0
    };
  }
}

// Advanced cache instances
const roomCalculationCache = new AdvancedCalculationCache(100);
const renderingCache = new AdvancedCalculationCache(50);
const areaCalculationCache = new AdvancedCalculationCache(200); // New cache for area calculations

// 🔧 FIX: Cache invalidation tracking for room label UI issues
let lastKnownRoomCount = 0;
let forceNextCalculation = false;

// Function to force cache invalidation when new rooms are detected
export const invalidateRoomCalculationCache = (reason: string = 'manual') => {
  console.log(`🔧 CACHE INVALIDATION: Clearing room calculation cache - ${reason}`);
  roomCalculationCache.clear();
  forceNextCalculation = true;
};

// Function to detect if new rooms have been created and force cache refresh
export const detectNewRoomsAndInvalidateCache = (currentRoomCount: number) => {
  if (currentRoomCount > lastKnownRoomCount) {
    console.log(`🔧 NEW ROOM DETECTION: Room count increased from ${lastKnownRoomCount} to ${currentRoomCount}`);
    invalidateRoomCalculationCache('new room detected');
    lastKnownRoomCount = currentRoomCount;
    return true;
  }
  lastKnownRoomCount = currentRoomCount;
  return false;
};

// Generate stable hash for caching
function generateWallsHash(walls: Wall[], customLabels: Record<string, string> = {}): string {
  const wallsString = walls
    .map(w => `${w.start.x.toFixed(1)},${w.start.y.toFixed(1)}-${w.end.x.toFixed(1)},${w.end.y.toFixed(1)}-${w.type}`)
    .sort()
    .join('|');
  const labelsString = Object.keys(customLabels).sort().map(k => `${k}:${customLabels[k]}`).join('|');
  return `${wallsString}##${labelsString}`;
}

// Advanced memoized room calculation with cache management
export const memoizedCalculateRooms = (
  walls: Wall[],
  roomCustomLabels: Record<string, string> = {},
  context: RoomCalculationContext = 'geometryUpdate',
  currentFloor?: number,
  labelsOnlyOperation?: { current: boolean },
  creationContext?: {
    isFromEndpoint?: boolean;
    lastRoomCount?: number;
    expectedNewRooms?: number;
    originalRoomCount?: number; // Track original room count for endpoint creation
  },
  isPositioningRoom?: boolean
): Room[] => {
  const startTime = RoomCalculationPerformanceMonitor.startTiming();
  
  // TEMPORARY: Return empty array to disable room calculations
  if (DISABLE_CALCULATIONS_FOR_DEBUG) {
    console.log('🚫 DEBUG: Room calculations disabled');
    RoomCalculationPerformanceMonitor.endTiming(startTime, walls.length, true);
    return [];
  }

  // 🔧 RACE CONDITION PROTECTION: During label-only operations, completely skip room calculations
  // to prevent overriding user-selected labels with default names
  // EXCEPTIONS: 
  // 1. During AR room positioning, we MUST calculate to show the positioning button
  // 2. During explicit room creation, we MUST calculate to trigger the label prompt
  const isExplicitRoomCreation = context === 'explicitRoomCreation';
  
  if (labelsOnlyOperation?.current && !isPositioningRoom && !isExplicitRoomCreation) {
    console.log('🔧 RACE CONDITION PROTECTION: Skipping ALL room calculations during label-only operation to prevent label override');
    RoomCalculationPerformanceMonitor.endTiming(startTime, walls.length, true);
    // Return empty array to prevent any room calculations from interfering with label assignment
    return [];
  }
  
  if (labelsOnlyOperation?.current && isPositioningRoom) {
    console.log('🔧 POSITIONING MODE OVERRIDE: Forcing room calculation despite label-only operation for AR positioning UI');
  }
  
  if (labelsOnlyOperation?.current && isExplicitRoomCreation) {
    console.log('🏠 EXPLICIT ROOM CREATION OVERRIDE: Forcing room calculation despite label-only operation for prompt detection');
  }

  if (!walls || walls.length === 0) {
    RoomCalculationPerformanceMonitor.endTiming(startTime, 0);
    return [];
  }

  // 🚀 PERFORMANCE: Adaptive complexity check
  if (RoomCalculationPerformanceMonitor.shouldSkipComplexCalculation(walls.length)) {
    console.warn(`⚠️ ADAPTIVE PERFORMANCE: Skipping room detection for ${walls.length} walls (performance-based threshold)`);
    RoomCalculationPerformanceMonitor.endTiming(startTime, walls.length, true);
    return [];
  }

  const cacheKey = `${generateWallsHash(walls, roomCustomLabels)}_${context}_${currentFloor || 'nofloor'}_${creationContext?.isFromEndpoint || false}`;
  
  // 🔧 FIX: Skip cache if we're forcing calculation or detecting new rooms
  const shouldSkipCache = forceNextCalculation || context === 'explicitRoomCreation';
  
  // Check cache first (unless we're forcing recalculation)
  const cached = !shouldSkipCache ? roomCalculationCache.get(cacheKey) : null;
  if (cached && !shouldSkipCache) {
    RoomCalculationPerformanceMonitor.endTiming(startTime, walls.length);
    return cached;
  }
  
  // Reset force flag after use
  if (forceNextCalculation) {
    console.log('🔧 CACHE BYPASS: Forcing fresh room calculation for UI synchronization');
    forceNextCalculation = false;
  }

  // Calculate and cache result
  console.log('🔧 MEMOIZED CALCULATION DEBUG: Calculating rooms', {
    cacheBypassed: shouldSkipCache,
    reason: shouldSkipCache ? (forceNextCalculation ? 'forced' : 'explicit creation') : 'normal',
    wallCount: walls.length,
    context,
    currentFloor,
    creationContext,
    cacheKey: cacheKey.substring(0, 100) + '...' // Truncate for readability
  });
  
  const result = calculateRooms(walls, roomCustomLabels, context, currentFloor, creationContext);
  roomCalculationCache.set(cacheKey, result);
  
  console.log('🔧 MEMOIZED CALCULATION DEBUG: Result', {
    resultCount: result.length,
    rooms: result.map(r => ({ name: r.name, area: r.area.toFixed(2) }))
  });
  
  RoomCalculationPerformanceMonitor.endTiming(startTime, walls.length);
  return result;
};



// Debounced function factory with cleanup
export const createSmartDebounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number,
  immediate: boolean = false
): T & { cancel: () => void; flush: () => void } => {
  let timeoutId: NodeJS.Timeout | null = null;
  let lastArgs: Parameters<T> | null = null;
  let lastResult: ReturnType<T>;

  const debounced = ((...args: Parameters<T>) => {
    lastArgs = args;
    
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (immediate && !timeoutId) {
      lastResult = func(...args);
      timeoutId = setTimeout(() => {
        timeoutId = null;
      }, delay);
      return lastResult;
    }

    timeoutId = setTimeout(() => {
      lastResult = func(...args);
      timeoutId = null;
    }, delay);
    
    return lastResult;
  }) as T & { cancel: () => void; flush: () => void };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  debounced.flush = () => {
    if (timeoutId && lastArgs) {
      clearTimeout(timeoutId);
      lastResult = func(...lastArgs);
      timeoutId = null;
    }
    return lastResult;
  };

  return debounced;
};

// Enhanced viewport culling for better performance
export const getVisibleElements = (
  elements: FloorPlanElement[],
  viewOffset: Point,
  scale: number,
  canvasWidth: number,
  canvasHeight: number
): FloorPlanElement[] => {
  if (!elements || elements.length === 0) return [];

  // Calculate visible bounds with margin for smooth scrolling
  const margin = 100; // pixels
  const visibleBounds = {
    minX: viewOffset.x - margin / scale,
    maxX: viewOffset.x + (canvasWidth + margin) / scale,
    minY: viewOffset.y - margin / scale,
    maxY: viewOffset.y + (canvasHeight + margin) / scale
  };

  return elements.filter(element => {
    // Skip walls as they don't have a position property
    if ('start' in element && 'end' in element) {
      return true; // Walls are handled separately in getVisibleWalls
    }
    
    // Check if element has position property (all non-wall elements do)
    if ('position' in element) {
      const pos = element.position;
      return pos.x >= visibleBounds.minX && 
             pos.x <= visibleBounds.maxX && 
             pos.y >= visibleBounds.minY && 
             pos.y <= visibleBounds.maxY;
    }
    
    return true; // Include elements that don't have position for safety
  });
};

// Enhanced wall culling for rendering optimization
export const getVisibleWalls = (
  walls: Wall[],
  viewOffset: Point,
  scale: number,
  canvasWidth: number,
  canvasHeight: number
): Wall[] => {
  if (!walls || walls.length === 0) return [];

  // Calculate visible bounds with margin
  const margin = 50; // pixels
  const visibleBounds = {
    minX: viewOffset.x - margin / scale,
    maxX: viewOffset.x + (canvasWidth + margin) / scale,
    minY: viewOffset.y - margin / scale,
    maxY: viewOffset.y + (canvasHeight + margin) / scale
  };

  return walls.filter(wall => {
    // Check if wall intersects with visible bounds
    const wallMinX = Math.min(wall.start.x, wall.end.x);
    const wallMaxX = Math.max(wall.start.x, wall.end.x);
    const wallMinY = Math.min(wall.start.y, wall.end.y);
    const wallMaxY = Math.max(wall.start.y, wall.end.y);

    return !(wallMaxX < visibleBounds.minX || 
             wallMinX > visibleBounds.maxX || 
             wallMaxY < visibleBounds.minY || 
             wallMinY > visibleBounds.maxY);
  });
};

// Performance monitoring class
export class PerformanceMonitor {
  private static measurements = new Map<string, number[]>();
  private static readonly MAX_MEASUREMENTS = 100;
  
  static startMeasurement(label: string): number {
    return performance.now();
  }
  
  static endMeasurement(label: string, startTime: number): void {
    const duration = performance.now() - startTime;
    
    if (!this.measurements.has(label)) {
      this.measurements.set(label, []);
    }
    
    const times = this.measurements.get(label)!;
    times.push(duration);
    
    // Keep only the latest measurements
    if (times.length > this.MAX_MEASUREMENTS) {
      times.shift();
    }
  }

  static getMetrics(label: string): { average: number; count: number; max: number } | null {
    const times = this.measurements.get(label);
    if (!times || times.length === 0) return null;
    
    const average = times.reduce((sum, time) => sum + time, 0) / times.length;
    const max = Math.max(...times);
    
    return { average, count: times.length, max };
  }
  
  static getAllMetrics(): Record<string, { average: number; count: number; max: number }> {
    const metrics: Record<string, { average: number; count: number; max: number }> = {};
    
    this.measurements.forEach((times, label) => {
      if (times.length > 0) {
        const average = times.reduce((sum, time) => sum + time, 0) / times.length;
        const max = Math.max(...times);
        metrics[label] = { average, count: times.length, max };
      }
    });
    
    return metrics;
  }

  static clearMetrics(): void {
    this.measurements.clear();
  }
}

// RequestAnimationFrame-based render scheduler
export class RenderScheduler {
  private static pendingRender = false;
  private static renderCallbacks: (() => void)[] = [];
  private static highPriorityCallbacks: (() => void)[] = [];
  
  static scheduleRender(callback: () => void, highPriority: boolean = false): void {
    if (highPriority) {
      this.highPriorityCallbacks.push(callback);
    } else {
      this.renderCallbacks.push(callback);
    }
    
    if (!this.pendingRender) {
      this.pendingRender = true;
      requestAnimationFrame(() => this.executeRender());
    }
  }
  
  private static executeRender(): void {
    // Execute high priority callbacks first
    const highPrioCallbacks = [...this.highPriorityCallbacks];
    const normalCallbacks = [...this.renderCallbacks];
    
    this.highPriorityCallbacks.length = 0;
    this.renderCallbacks.length = 0;
    this.pendingRender = false;
    
    // Execute high priority first
    highPrioCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('High priority render callback error:', error);
      }
    });
    
    // Execute normal priority
    normalCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Render callback error:', error);
      }
    });
  }
  
  static cancelPendingRenders(): void {
    this.renderCallbacks.length = 0;
    this.highPriorityCallbacks.length = 0;
    this.pendingRender = false;
  }
}

// Level-of-detail rendering helper
export const shouldRenderDetail = (scale: number, detailLevel: 'low' | 'medium' | 'high'): boolean => {
  switch (detailLevel) {
    case 'low':
      return scale >= 0.25;
    case 'medium':
      return scale >= 0.5;
    case 'high':
      return scale >= 1.0;
    default:
      return true;
  }
};

// Cleanup utilities
export const clearAllCaches = (): void => {
  roomCalculationCache.clear();
  renderingCache.clear();
  areaCalculationCache.clear();
  PerformanceMonitor.clearMetrics();
};

// Throttled drawing function with frame skipping
export const createThrottledDraw = (
  drawFunction: () => void,
  targetFPS: number = 60
): (() => void) => {
  const frameTime = 1000 / targetFPS;
  let lastFrameTime = 0;
  let animationId: number | null = null;
  
  return () => {
    if (animationId) {
      cancelAnimationFrame(animationId);
    }
    
    animationId = requestAnimationFrame((currentTime) => {
      if (currentTime - lastFrameTime >= frameTime) {
        drawFunction();
        lastFrameTime = currentTime;
      }
    });
  };
};

// Optimized area calculation with caching
export const memoizedCalculateArea = (walls: Wall[]): number => {
  if (!walls || walls.length < 3) return 0;
  
  // Generate stable hash for walls configuration
  const wallsHash = walls
    .map(w => `${w.start.x.toFixed(2)},${w.start.y.toFixed(2)}-${w.end.x.toFixed(2)},${w.end.y.toFixed(2)}`)
    .sort()
    .join('|');
  
  // Check cache first
  const cached = areaCalculationCache.get(wallsHash);
  if (cached !== null) {
    return cached;
  }
  
  // Calculate area using optimized method
  const area = calculateAreaOptimized(walls);
  areaCalculationCache.set(wallsHash, area);
  
  return area;
};

// Optimized area calculation algorithm
const calculateAreaOptimized = (walls: Wall[]): number => {
  if (walls.length < 3) return 0;
  
  // Fast path: Try rectangular calculation first (most common case)
  const bounds = getRoomBoundsOptimized(walls);
  const widthPixels = bounds.maxX - bounds.minX;
  const heightPixels = bounds.maxY - bounds.minY;
  
  // Check if it's likely rectangular by comparing wall count vs expected
  const expectedWallsForRectangle = 4;
  if (walls.length === expectedWallsForRectangle) {
    // For 4-wall rooms, try rectangular calculation
    const widthMeters = Math.round((widthPixels / PIXELS_PER_METER) * 100) / 100;
    const heightMeters = Math.round((heightPixels / PIXELS_PER_METER) * 100) / 100;
    const rectangularArea = widthMeters * heightMeters;
    
    // Quick validation: check if walls are approximately axis-aligned
    const isRectangular = walls.every(wall => {
      const deltaX = Math.abs(wall.end.x - wall.start.x);
      const deltaY = Math.abs(wall.end.y - wall.start.y);
      return deltaX < 1 || deltaY < 1; // Either horizontal or vertical with small tolerance
    });
    
    if (isRectangular) {
      return rectangularArea;
    }
  }
  
  // Fallback: Use optimized shoelace formula
  return calculateShoelaceArea(walls);
};

// Optimized bounds calculation
const getRoomBoundsOptimized = (walls: Wall[]): { minX: number; maxX: number; minY: number; maxY: number } => {
  if (walls.length === 0) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  
  // Single pass through all points
  for (const wall of walls) {
    // Start point
    if (wall.start.x < minX) minX = wall.start.x;
    if (wall.start.x > maxX) maxX = wall.start.x;
    if (wall.start.y < minY) minY = wall.start.y;
    if (wall.start.y > maxY) maxY = wall.start.y;
    
    // End point
    if (wall.end.x < minX) minX = wall.end.x;
    if (wall.end.x > maxX) maxX = wall.end.x;
    if (wall.end.y < minY) minY = wall.end.y;
    if (wall.end.y > maxY) maxY = wall.end.y;
  }
  
  return { minX, maxX, minY, maxY };
};

// Optimized shoelace formula
const calculateShoelaceArea = (walls: Wall[]): number => {
  let area = 0;
  const len = walls.length;
  
  // Optimized shoelace algorithm - single loop
  for (let i = 0; i < len; i++) {
    const j = (i + 1) % len;
    area += walls[i].start.x * walls[j].start.y;
    area -= walls[j].start.x * walls[i].start.y;
  }
  
  const shoelaceArea = Math.abs(area * 0.5) / (PIXELS_PER_METER * PIXELS_PER_METER);
  return Math.round(shoelaceArea * 100) / 100;
}; 